import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DynamicCrudPage from '../../components/DynamicCrudPage';
import { useTeams, useTeamMemberPermissions, useTags, SCREENS } from '../../hooks';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';

const TeamsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const teamMemberPermissions = useTeamMemberPermissions();

  // Custom form fields with validation
  const customFormFields = useCallback((fields, additionalData, isRootUser, t) => {
    const { teams, tags } = additionalData;
    const users = teams?.users || [];
    const companies = teams?.companies || [];
    const groups = teams?.groups || [];
    const tagsList = tags?.tags || [];

    const validateTeamGroup = (value, formData) => {
      if (value && value !== '') {
        const selectedGroup = groups.find(g => g.id === parseInt(value));
        
        if (selectedGroup && (!selectedGroup.group_manager_id || selectedGroup.group_manager_id === null)) {
          return t('teams.validation.groupManagerRequired');
        }
      }
      return null;
    };

    const getEligibleTeamManagers = () => {
      return users.filter(user => 
        ['ROOT', 'DIRECTOR', 'GROUP_MANAGER', 'TEAM_MANAGER'].includes(user.role)
      );
    };

    const eligibleManagers = getEligibleTeamManagers();
    
    return fields
      .map(field => {
        if (field.name === 'companyId') {
          return {
            ...field,
            options: companies.map(c => ({ value: c.id, label: c.name })),
            showIf: () => isRootUser,
          };
        }
        if (field.name === 'groupId') {
          return {
            ...field,
            options: [
              { value: '', label: t('teams.form.selectGroup') },
              ...groups.map(g => ({ 
                value: g.id, 
                label: `${g.name} ${(!g.group_manager_id || g.group_manager_id === null) ? t('teams.form.noManager') : ''}` 
              })),
            ],
            validation: {
              custom: validateTeamGroup,
            },
          };
        }
        if (field.name === 'teamManagerId') {
          return {
            ...field,
            options: [
              { value: '', label: t('teams.form.selectTeamManager') },
              ...eligibleManagers.map(u => ({ 
                value: u.id, 
                label: `${u.first_name} ${u.last_name} (${u.role})` 
              })),
            ],
          };
        }
        if (field.name === 'tagIds') {
          const teamTags = tagsList.filter(tag => tag.tags_page === 'TEAMS');
          return {
            ...field,
            options: teamTags.map(tag => ({
              value: tag.id,
              label: tag.name
            })),
          };
        }
        return field;
      })
      .filter(field => (field.name === 'companyId' ? isRootUser : true));
  }, []);

  // Custom filter configs
  const customFilterConfigs = useCallback((filters, data, isRootUser, t) => {
    return filters.filter(f => f.showIfRoot ? isRootUser : true);
  }, []);

  // Custom row actions for teams
  const customRowActions = useCallback((row) => {
    const actions = [];
    
    if (teamMemberPermissions.canView) {
      actions.push({
        type: 'members',
        label: 'table.actions.members',
        color: 'info',
        icon: <PeopleIcon />,
      });
    }

    actions.push(
      {
        type: 'edit',
        label: 'table.actions.edit',
        color: 'primary',
      },
      {
        type: 'delete',
        label: 'table.actions.delete',
        color: 'error',
      }
    );
    
    return actions;
  }, [t, teamMemberPermissions.canView]);

  // Handle row actions
  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'members') {
      // Check if user has view permission for team members page
      if (!teamMemberPermissions.canView) {
        return true; // Prevent navigation
      }
      navigate(`/teams/${row.id}/members`);
      return true; // Prevent default action
    }
    return false; // Allow default action for edit/delete
  }, [navigate, teamMemberPermissions.canView]);

  // Transform edit data for form
  const transformEditData = useCallback((teamData) => {
    if (!teamData) return {};
    
    return {
      ...teamData,
      companyId: teamData.company_id,
      groupId: teamData.group_id,
      teamManagerId: teamData.team_manager_id,
      tagIds: teamData.team_tags?.map(tt => tt.tag_id) || [],
    };
  }, []);

  // Transform form data for submission
  const transformFormData = useCallback((formData) => {
    return {
      ...formData,
      company_id: formData.companyId,
      group_id: formData.groupId,
      team_manager_id: formData.teamManagerId,
    };
  }, []);

  return (
    <DynamicCrudPage
      entityType="teams"
      screen={SCREENS.TEAMS}
      hook={useTeams}
      additionalHooks={{ teams: useTeams, tags: useTags }}
      customFormFields={customFormFields}
      customFilterConfigs={customFilterConfigs}
      addButtonTextKey="table.actions.addTeam"
      addButtonIcon={<AddIcon />}
      entityTypeForDataTable="team"
      customTransformEditData={transformEditData}
      customTransformFormData={transformFormData}
      showAddButton={true}
      customRowActions={customRowActions}
      onRowAction={handleRowAction}
    />
  );
};

export default TeamsPage;
